// utils/registrationMapper.js

import personalCodeValidation from '@/components/hooks/personalCodeValidation';
import { readReferralVisitId } from '@/v2/utils/referralTracking';

export const buildUiPayload = ({
  userInfo,
  paymentMerged,
  selectedTimeSlot,
  svc,
  userState,
  childSelected,
  selectedChildEspbiEsi,
  consultationInfo, // pass from CartPage
}) => ({
  user: userInfo ?? {},
  payment: paymentMerged ?? {},
  slot: selectedTimeSlot ?? null,
  svc: svc ?? null,
  userState: userState ?? null,
  childSelected: !!childSelected,
  selectedChildEspbiEsi: selectedChildEspbiEsi ?? '',
  consultation: consultationInfo ?? {},
});

// birth-date helper (4th item in tuple)
const [, , , birthDateFromPersonalCode] = personalCodeValidation();

// helpers
const strOrEmpty = (v) => (v === undefined || v === null ? '' : String(v));
const nullIfEmptyArray = (arr) =>
  Array.isArray(arr) ? (arr.length ? arr : null) : null;

const toNumberOrNull = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const deriveIds = (svc) => {
  const s = Array.isArray(svc) ? s?.[0] : svc;
  return {
    workplaceId: s?.workplaceId ?? s?.workPlaceId ?? s?.workplace?.id ?? 0,
    serviceId: s?.serviceId ?? s?.id ?? s?.service?.id ?? 0,
  };
};

// price helpers (legacy fallbacks)
const calcTotalPrice = (sp) => {
  if (!sp || typeof sp !== 'object') return null;
  const parts = [
    sp.MainServicePrice,
    sp.SecondaryServicePrice,
    sp.ExtraServicePrice,
  ]
    .map((x) => (Number.isFinite(Number(x)) ? Number(x) : null))
    .filter((x) => x !== null);

  return parts.length ? parts.reduce((a, b) => a + b, 0) : null;
};

const hasCouponsApplied = (p) =>
  (Array.isArray(p?.couponIds) && p.couponIds.length > 0) ||
  (typeof p?.discountCodeNumber === 'string' &&
    p.discountCodeNumber.trim() !== '');

// TimeSlot normalization WITHOUT reformatting dates — copy as-is
const normalizeSlot = (slot) => {
  if (!slot) return null;
  return {
    id: slot.timeSlotId ?? slot.id ?? slot.timeSlotID ?? 0,
    startRaw: slot.startDate ?? slot.start ?? slot.startDateTime ?? null,
    endRaw: slot.endDate ?? slot.end ?? slot.endDateTime ?? null,
    isPaid: typeof slot.isPaid === 'boolean' ? slot.isPaid : null,
    isExtra: typeof slot.isExtra === 'boolean' ? slot.isExtra : null,
    tags: Array.isArray(slot.tags) ? slot.tags : null,
    researchAreaId: slot.researchAreaId ?? slot.researchArea?.id ?? null,
  };
};

// Backend PaymentType string (separate from TimeSlotPaymentType enum on service)
const deducePaymentType = (slotLite, p) => {
  const method = (p?.paymentId || '').trim(); // 'EBank' | 'Insurance' | 'Juridical' | 'OnSite'
  // 5) Explicit selections override others
  if (method === 'Insurance' && p?.insurance) return 'Social';
  if (method === 'Juridical' && p?.company) return 'Juridical';
  if (method === 'OnSite') return 'OnSite';
  if (method === 'Stebby') return 'Stebby';
  // Determine PSDF slot
  const isPsdfSlot =
    !!slotLite && slotLite.isPaid === false && slotLite.isExtra !== true;

  // We prefer numbers coming from live pricing:
  // - p.computedTotal: final total AFTER coupons/discounts
  // - p.couponsAppliedTotal: sum of coupons used (from repricing DTO)
  const computedTotal = toNumberOrNull(p?.computedTotal) ?? null;
  const couponsAppliedTotalRaw = toNumberOrNull(p?.couponsAppliedTotal) ?? 0;

  // Legacy fallback for "base" price if no live numbers are present
  const legacyBase = calcTotalPrice(p?.servicePrice); // may be null

  // Base after discounts but BEFORE coupons:
  // If we have computedTotal and coupons sum, base = computedTotal + couponsAppliedTotal.
  // Otherwise, fall back to whatever legacy provided (may be null).
  const baseBeforeCoupons =
    computedTotal !== null
      ? computedTotal + (couponsAppliedTotalRaw || 0)
      : legacyBase;

  // Safeguards
  const baseIsNumber =
    typeof baseBeforeCoupons === 'number' && isFinite(baseBeforeCoupons);
  const base = baseIsNumber ? Math.max(0, baseBeforeCoupons) : null;
  const couponsAppliedTotal = Math.max(
    0,
    Math.min(base ?? Number.POSITIVE_INFINITY, couponsAppliedTotalRaw || 0),
  );
  const finalTotal =
    computedTotal !== null
      ? Math.max(0, computedTotal)
      : base !== null
        ? Math.max(0, base - couponsAppliedTotal)
        : null;

  // Helper flags
  const zeroOrLess = typeof finalTotal === 'number' ? finalTotal <= 0 : false;
  const couponsUsed = couponsAppliedTotal > 0;

  // Apply rules in priority:
  // 1) PSDF slot with no additional charges (base 0) => 'PSDF'
  if (isPsdfSlot && (base === null || base === 0)) return 'PSDF';

  // 2) PSDF slot with base > 0 and still needs payment after coupons => 'Paysera'
  if (isPsdfSlot && base !== null && base > 0 && !zeroOrLess) return 'Paysera';

  // 3) PSDF slot with base > 0 but fully covered by coupons => 'PSDFWithCoupons'
  if (isPsdfSlot && base !== null && base > 0 && zeroOrLess && couponsUsed)
    return 'PSDFWithCoupons';

  // 4) Not PSDF slot, total goes to 0 by discounts/coupons => 'Coupons'
  if (!isPsdfSlot && zeroOrLess) return 'Coupons';

  // Default online payment
  return 'Paysera';
};

const DEFAULT_BIRTHDATE_ISO = '1900-01-01T00:00:00.000Z';

// Local enum mirror for service-level TimeSlotPaymentType
const TSP = Object.freeze({
  Undefined: 0,
  PSDF: 1,
  PAID: 2,
  EXTRAPAID: 3,
});

// derive PrimaryVisit: default true; radio 'opt2' means NOT primary
const derivePrimaryVisit = (c) => {
  if (typeof c?.primaryVisit === 'boolean') return c.primaryVisit;
  const sel = (c?.visitType ?? '').toString();
  if (sel === 'secondary') return false; // repeat consultation
  if (sel === 'primary') return true; // primary
  return true; // default
};

export const toRegistrationInput = (ui) => {
  const u = ui?.user ?? {};
  const p = ui?.payment ?? {};
  const c = ui?.consultation ?? {};
  const normSlot = normalizeSlot(ui?.slot);
  // NOTE: removed single deriveIds(ui?.svc) usage — we'll derive per-service below.

  // discounts
  const discountCode =
    typeof p.discountCodeNumber === 'string' ? p.discountCodeNumber : '';
  const couponNumbers = Array.isArray(p.couponIds) ? p.couponIds : [];

  // Determine numbers used for payment-type logic and CouponSum
  const computedTotal = toNumberOrNull(p?.computedTotal); // final after coupons
  const couponsAppliedTotalRaw = toNumberOrNull(p?.couponsAppliedTotal) ?? 0;
  const legacyBase = calcTotalPrice(p?.servicePrice);
  const baseBeforeCoupons =
    computedTotal !== null
      ? computedTotal + (couponsAppliedTotalRaw || 0)
      : legacyBase;

  const baseIsNumber =
    typeof baseBeforeCoupons === 'number' && isFinite(baseBeforeCoupons);
  const base = baseIsNumber ? Math.max(0, baseBeforeCoupons) : null;
  const couponsAppliedTotal = Math.max(
    0,
    Math.min(base ?? Number.POSITIVE_INFINITY, couponsAppliedTotalRaw || 0),
  );

  // PaymentType (top-level string)
  const paymentType = deducePaymentType(
    normSlot ? { isPaid: normSlot.isPaid, isExtra: normSlot.isExtra } : null,
    {
      ...p,
      computedTotal,
      couponsAppliedTotal, // use the capped one in the logic as well
    },
  );

  // backend TimeSlot — copy 1:1 (no reformatting)
  const timeSlotObj =
    normSlot &&
    (normSlot.id ||
      normSlot.startRaw ||
      normSlot.endRaw ||
      normSlot.tags ||
      normSlot.researchAreaId)
      ? {
          TimeSlotId: normSlot.id || 0,
          StartDate: normSlot.startRaw ?? null,
          EndDate: normSlot.endRaw ?? null,
          Tags: nullIfEmptyArray(normSlot.tags),
          ResearchAreaId: normSlot.researchAreaId ?? null,
        }
      : null;

  // Patient (payer) birthdate via helper
  const payerId = strOrEmpty(u.payerPersonalCode);
  const payerBirth = payerId ? birthDateFromPersonalCode(payerId) : null;
  const patientBirthDate =
    payerBirth instanceof Date && !isNaN(payerBirth.getTime())
      ? payerBirth.toISOString()
      : typeof payerBirth === 'string' && payerBirth.trim() !== ''
        ? payerBirth
        : DEFAULT_BIRTHDATE_ISO;

  // Child (top-level)
  const childName = strOrEmpty(u.patientFirstName);
  const childSurname = strOrEmpty(u.patientLastName);
  const childIdNumber = strOrEmpty(u.patientPersonalCode);

  // Child birth date (service-level) via helper
  const childBirthParsed = childIdNumber
    ? birthDateFromPersonalCode(childIdNumber)
    : null;
  const childBirthDateISO =
    childBirthParsed instanceof Date && !isNaN(childBirthParsed.getTime())
      ? childBirthParsed.toISOString()
      : typeof childBirthParsed === 'string' && childBirthParsed.trim() !== ''
        ? childBirthParsed
        : null;

  // child ownership / ESI logic using contacts / dropdown flags
  const contacts = Array.isArray(ui?.userState?.contacts)
    ? ui.userState.contacts
    : [];

  let haveKid;
  let correctChild;
  let espbiEsi;

  if (contacts.length > 0) {
    if (childIdNumber) {
      haveKid = true;
      const match = contacts.find((c0) => c0?.personalCode === childIdNumber);
      if (match) {
        correctChild = true;
        espbiEsi = strOrEmpty(match.espbiEsi);
      }
    } else if (ui?.childSelected) {
      haveKid = true;
      correctChild = true;
      espbiEsi = strOrEmpty(ui?.selectedChildEspbiEsi);
    }
  }

  // === Consultation mapping (from context) ===
  const patientComment = (c.reason ?? c.patientComment ?? '').trim();
  const serviceQuestionId =
    c.paidReasonId != null && String(c.paidReasonId).trim() !== ''
      ? Number(c.paidReasonId)
      : c.serviceQuestionId != null && String(c.serviceQuestionId).trim() !== ''
        ? Number(c.serviceQuestionId)
        : null;

  const contraindicationIds = Array.isArray(c.contraindicationQuestionIds)
    ? c.contraindicationQuestionIds
        .map((n) => Number(n))
        .filter((n) => Number.isFinite(n))
    : [];

  const eHealth = !!(c.eHealth ?? c.eHealthReferral);

  // === TimeSlotPaymentType logic (exactly as legacy)
  const paidLabFlag =
    typeof c.isPaidLaboratoryService === 'boolean'
      ? c.isPaidLaboratoryService
      : null;

  let timeSlotPaymentType = TSP.Undefined;
  if (paidLabFlag === true) {
    timeSlotPaymentType = TSP.PAID;
  } else if (paidLabFlag === false) {
    timeSlotPaymentType = TSP.PSDF;
  } else if (normSlot?.isExtra === true) {
    timeSlotPaymentType = TSP.EXTRAPAID;
  } else if (normSlot?.isPaid === true) {
    timeSlotPaymentType = TSP.PAID;
  } else {
    timeSlotPaymentType = TSP.PSDF;
  }

  // === PrimaryVisit: default true; radio 'opt2' toggles to false
  const primaryVisit = derivePrimaryVisit(c);
  // ⬇️ collect FileIds from Consultation context (if provided)
  const fileIdsFromContext = Array.isArray(c?.files)
    ? c.files
        .map((f) => {
          // Case: file object like {fileName, fileUid}
          if (f && typeof f === 'object' && 'fileUid' in f) {
            return f.fileUid?.trim();
          }

          // Case: already a string id
          if (typeof f === 'string') {
            return f.trim();
          }

          return null;
        })
        .filter(Boolean)
    : [];

  // -------------------------------
  // NEW: build an array of registration services
  // preserve original single-item semantics when ui.svc is not an array
  const servicesArray = Array.isArray(ui?.svc)
    ? ui.svc
    : ui?.svc
      ? [ui.svc]
      : [ui?.svc]; // preserves original behavior (single item with zeros if svc is null)

  const registrationServices = servicesArray.map((svcItem) => {
    const ids = deriveIds(svcItem);

    return {
      WorkplaceId: ids.workplaceId || 0,
      ServiceId: ids.serviceId || 0,
      TimeSlotId: normSlot?.id || 0,

      // child
      ChildName: childName,
      ChildSurname: childSurname,
      ChildBirthDate: childBirthDateISO,
      ChildIdNumber: childIdNumber,

      // ownership / validations
      HaveKid: haveKid,
      CorrectChild: correctChild,
      EspbiEsi: espbiEsi,

      // consultation
      PatientComment: patientComment,
      ServiceQuestionId: Number.isFinite(serviceQuestionId)
        ? serviceQuestionId
        : null,
      ContraindicationQuestionIds: contraindicationIds, // [] (non-null)
      PrimaryVisit: primaryVisit,
      FileIds: fileIdsFromContext.length ? fileIdsFromContext : [],
      eHealth: eHealth,

      // legacy-derived flags
      IsPaidLaboratoryService: normSlot?.isPaid, // can be true/false/null
      TimeSlotPaymentType: timeSlotPaymentType, // 0..3
    };
  });

  // Clear/keep Juridical/Social fields depending on chosen type
  const isJuridical = paymentType === 'Juridical';
  const isSocial = paymentType === 'Social';

  return {
    // Patient (payer)
    ReferralVisitId: readReferralVisitId(),
    PatientName: strOrEmpty(u.payerFirstName),
    PatientSurname: strOrEmpty(u.payerLastName),
    PatientEmail: strOrEmpty(u.payerEmail),
    PatientPhoneNumber: strOrEmpty(u.payerPhone),
    PatientBirthDate: patientBirthDate,
    PatientIdNumber: strOrEmpty(u.payerPersonalCode),

    // Child (top-level; no birth date)
    ChildName: childName,
    ChildSurname: childSurname,
    ChildIdNumber: childIdNumber,

    // Discounts
    DiscountCode: discountCode,
    CouponNumbers: couponNumbers,
    // 1) total coupon applied sum, capped to base (after discounts, before coupons)
    CouponSum: couponsAppliedTotal,

    // Front URLs
    FrontUrls: {
      CancelFrontURL: '/cart/cancel',
      AcceptFrontUrl: '/cart/success',
      ErrorFrontUrl: '/cart/error',
    },

    // Payment
    PaymentType: paymentType,

    // TimeSlot
    TimeSlot: timeSlotObj,

    // Services (now supports multiple from ui.svc)
    RegistrationServices: registrationServices,

    // ⬇️ top-level extras (clear when not selected)
    InsuranceCompany: isSocial ? strOrEmpty(p.insurance) : '',
    JuridicalPerson: isJuridical ? strOrEmpty(p.company) : '',

    // ⬇️ ServiceUrl (same as CancelFrontURL)
    ServiceUrl: '/cart/cancel',
  };
};
